import { useQuery, useQueryClient } from '@tanstack/react-query';
import { fetchUserProfile, type UserProfile } from '@/services/profile';

export const CURRENT_PROFILE_KEY = 'current-profile';

export function useCurrentProfile(userId: string | undefined) {
  return useQuery({
    queryKey: [CURRENT_PROFILE_KEY, userId],
    queryFn: () => fetchUserProfile(userId!),
    enabled: !!userId,
    staleTime: 10 * 60_000,
    gcTime: 30 * 60_000,
  });
}

/** Call after profile save to sync all consumers (navbar, ranking, etc.) */
export function useInvalidateCurrentProfile() {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: [CURRENT_PROFILE_KEY] });
}

/**
 * Patch the cached current-profile in place. Use when we already know the
 * authoritative new values and want to avoid a network round trip (and the
 * flicker that comes with stale refetches re-clobbering optimistic UI).
 */
export function usePatchCurrentProfile(userId: string | undefined) {
  const queryClient = useQueryClient();
  return (patch: Partial<UserProfile>) => {
    if (!userId) return;
    queryClient.setQueryData<UserProfile | null>(
      [CURRENT_PROFILE_KEY, userId],
      (prev) => (prev ? { ...prev, ...patch } : prev),
    );
  };
}
